import { Injectable, computed } from '@angular/core';
import {
  IIcLookupPaycodeForFixedTransactionDataDto,
  IcLookupDto,
  IcLookupPaycodeForFixedTransactionItemDto,
  IcLookupTypeEnumDto,
  LocalizedStringDto,
} from '@data-access/bulk-operations-api';
import { getTimelineItemsActiveInPeriod, getTranslationFromLocalizedString } from '@em-helpers';

import { IcServiceBase } from './ic-service.base';

@Injectable({ providedIn: 'root' })
export class IcPaycodesForFixedTransactionService extends IcServiceBase<
  IcLookupPaycodeForFixedTransactionItemDto,
  IcPaycodeForFixedTransaction
> {
  protected override lookupType = IcLookupTypeEnumDto.PaycodeForFt;

  protected override mapToLookupModel(
    value: IcLookupPaycodeForFixedTransactionItemDto,
  ): IcPaycodeForFixedTransaction {
    return {
      ...value.data,
      name: new LocalizedStringDto({
        english: value.data.name?.english ?? '',
        norwegian: value.data.name?.norwegian ?? '',
      }),
      description: getTranslationFromLocalizedString(value.data.description),
      displayName: `${value.data.customId} - ${getTranslationFromLocalizedString(value.data.name)}`,
      id: value.id,
    };
  }

  protected override getValuesFromLookupDto(
    lookup: IcLookupDto,
  ): IcLookupPaycodeForFixedTransactionItemDto[] {
    return lookup.paycodesForFixedTransaction;
  }

  public override readonly valuesSignal = computed<IcPaycodeForFixedTransaction[]>(() =>
    [...this.rawValuesSignal().values()]
      .map((item) => this.mapToLookupModel(item))
      .sort((a, b) => a.customId.localeCompare(b.customId, undefined, { numeric: true })),
  );

  public getPaycodesForDateRange(
    dateFrom: Date | null,
    dateTo: Date | null,
  ): IcPaycodeForFixedTransaction[] {
    if (!dateFrom) {
      return this.valuesSignal();
    }

    const allPaycodes = this.valuesSignal();
    return allPaycodes.filter((paycode) => {
      if (!paycode.timeline || paycode.timeline.length === 0) return true;

      const overlappingItems = getTimelineItemsActiveInPeriod(paycode.timeline, dateFrom, dateTo);
      return overlappingItems && overlappingItems.length > 0;
    });
  }
}

export type IcPaycodeForFixedTransaction = Omit<
  IIcLookupPaycodeForFixedTransactionDataDto,
  'description'
> & {
  id: string;
  displayName: string;
  description: string;
};
